import { supabase } from '../lib/supabase';

// Normalize error to always be a proper Error instance
function normalizeError(err, fallback) {
  if (err instanceof Error) return err;
  if (err && typeof err === 'object') {
    const msg = err?.message || fallback || 'Unknown error';
    return Object.assign(new Error(msg), err);
  }
  return new Error(err || fallback || 'Unknown error');
}

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      acc[camelKey] = toCamelCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Convert camelCase to snake_case
const toSnakeCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(item => toSnakeCase(item));
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj)?.reduce((acc, key) => {
      const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
      acc[snakeKey] = toSnakeCase(obj?.[key]);
      return acc;
    }, {});
  }
  return obj;
};

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  // Check PostgreSQL error code class (first 2 characters)
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    
    // Class 42 = Schema errors (syntax, undefined table/column, etc.)
    if (errorClass === '42') {
      return true;
    }
    
    // Class 08 = Connection errors - treat as schema/system errors
    if (errorClass === '08') {
      return true;
    }
  }
  
  // Fallback: Check error message patterns for schema issues
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const customerService = {
  // Get all customers
  async getAll() {
    try {
      const { data: customers, error } = await supabase
        ?.from('customers')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching customers');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }

      if (!customers || customers?.length === 0) {
        return { data: [], error: null };
      }

      // Fetch live projects and bids to compute accurate stats
      const { data: projects } = await supabase?.from('projects')?.select('customer_name, status, total_budget');
      const { data: bids } = await supabase?.from('bids')?.select('customer_name, status, total_amount');

      const enriched = customers?.map(customer => {
        const name = customer?.company_name?.toLowerCase();

        const customerProjects = projects?.filter(p =>
          p?.customer_name?.toLowerCase() === name
        ) || [];

        const customerBids = bids?.filter(b =>
          b?.customer_name?.toLowerCase() === name
        ) || [];

        const activeProjects = customerProjects?.filter(p =>
          p?.status === 'In Progress' || p?.status === 'Planning'
        )?.length;

        const totalValue = customerProjects?.reduce(
          (sum, p) => sum + (parseFloat(p?.total_budget) || 0), 0
        );

        const acceptedBids = customerBids?.filter(b =>
          b?.status === 'Accepted' ||
          b?.status === 'Converted to Project' ||
          b?.status === 'Converted to a Project'
        )?.length;

        const bidConversionRate = customerBids?.length > 0
          ? Math.round((acceptedBids / customerBids?.length) * 100)
          : 0;

        return {
          ...customer,
          active_projects: activeProjects,
          total_value: totalValue,
          bid_conversion_rate: bidConversionRate,
          total_bids: customerBids?.length,
        };
      });

      return { data: toCamelCase(enriched), error: null };
    } catch (error) {
      console.error('Error fetching customers:', error?.message);
      throw normalizeError(error, 'Error fetching customers');
    }
  },

  // Get single customer by ID
  async getById(id) {
    try {
      const { data, error } = await supabase
        ?.from('customers')
        ?.select('*')
        ?.eq('id', id)
        ?.maybeSingle();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching customer');
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching customer:', error?.message);
      throw normalizeError(error, 'Error fetching customer');
    }
  },

  // Create new customer
  async create(customerData) {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const customerToInsert = toSnakeCase({
        ...customerData,
        createdBy: user?.id
      });

      const { data, error } = await supabase
        ?.from('customers')
        ?.insert(customerToInsert)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error creating customer');
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error creating customer:', error?.message);
      throw normalizeError(error, 'Error creating customer');
    }
  },

  // Update customer
  async update(id, customerData) {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const customerToUpdate = toSnakeCase(customerData);

      const { data, error } = await supabase
        ?.from('customers')
        ?.update(customerToUpdate)
        ?.eq('id', id)
        ?.select()
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error updating customer');
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error updating customer:', error?.message);
      throw normalizeError(error, 'Error updating customer');
    }
  },

  // Delete customer
  async delete(id) {
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabase
        ?.from('customers')
        ?.delete()
        ?.eq('id', id);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error deleting customer');
        } else {
          console.log('Data error:', error?.message);
          return { error };
        }
      }
      return { error: null };
    } catch (error) {
      console.error('Error deleting customer:', error?.message);
      throw normalizeError(error, 'Error deleting customer');
    }
  },

  // Search customers by company name or contact person
  async search(searchTerm) {
    try {
      const { data, error } = await supabase
        ?.from('customers')
        ?.select('*')
        ?.or(`company_name.ilike.%${searchTerm}%,contact_person.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error searching customers');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error searching customers:', error?.message);
      throw normalizeError(error, 'Error searching customers');
    }
  },

  // Filter customers by active projects count
  async filterByActiveProjects(minProjects) {
    try {
      const { data, error } = await supabase
        ?.from('customers')
        ?.select('*')
        ?.gte('active_projects', minProjects)
        ?.order('active_projects', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error filtering customers');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error filtering customers:', error?.message);
      throw normalizeError(error, 'Error filtering customers');
    }
  },

  // Get customers with their bids and projects from separate tables
  async getCustomersWithRelations() {
    try {
      // Fetch all customers
      const { data: customers, error: customersError } = await supabase
        ?.from('customers')
        ?.select('*')
        ?.order('created_at', { ascending: false });

      if (customersError) {
        if (isSchemaError(customersError)) {
          console.error('Schema error:', customersError?.message);
          throw normalizeError(customersError, 'Schema error fetching customers with relations');
        }
        return { data: null, error: customersError };
      }

      // Fetch all bids
      const { data: bids, error: bidsError } = await supabase
        ?.from('bids')
        ?.select('*');

      if (bidsError && !isSchemaError(bidsError) && bidsError?.code !== 'PGRST116') {
        console.log('Bids fetch error:', bidsError?.message);
      }

      // Fetch all projects
      const { data: projects, error: projectsError } = await supabase
        ?.from('projects')
        ?.select('*');

      if (projectsError && !isSchemaError(projectsError) && projectsError?.code !== 'PGRST116') {
        console.log('Projects fetch error:', projectsError?.message);
      }

      // Combine data - match by customer_name
      const customersWithRelations = customers?.map(customer => {
        const customerBids = bids?.filter(bid => 
          bid?.customer_name?.toLowerCase() === customer?.company_name?.toLowerCase()
        ) || [];
        
        const customerProjects = projects?.filter(project => 
          project?.customer_name?.toLowerCase() === customer?.company_name?.toLowerCase()
        ) || [];

        return {
          ...customer,
          relatedBids: customerBids,
          relatedProjects: customerProjects,
          actualActiveProjects: customerProjects?.filter(p => 
            p?.status === 'In Progress' || p?.status === 'Planning'
          )?.length || 0,
          actualTotalBids: customerBids?.length || 0
        };
      });

      return { data: toCamelCase(customersWithRelations), error: null };
    } catch (error) {
      console.error('Error fetching customers with relations:', error?.message);
      throw normalizeError(error, 'Error fetching customers with relations');
    }
  },

  // Get bids for a specific customer by customer name
  async getBidsByCustomer(customerName) {
    try {
      const { data, error } = await supabase
        ?.from('bids')
        ?.select('*')
        ?.ilike('customer_name', customerName)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching bids by customer');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching bids for customer:', error?.message);
      throw normalizeError(error, 'Error fetching bids for customer');
    }
  },

  // Get projects for a specific customer by customer name
  async getProjectsByCustomer(customerName) {
    try {
      const { data, error } = await supabase
        ?.from('projects')
        ?.select('*')
        ?.ilike('customer_name', customerName)
        ?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error fetching projects by customer');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error fetching projects for customer:', error?.message);
      throw normalizeError(error, 'Error fetching projects for customer');
    }
  },

  // Advanced search across multiple fields with filters
  async advancedSearch(filters = {}) {
    try {
      let query = supabase?.from('customers')?.select('*');

      // Search term - searches across company_name, contact_person, email, phone, address
      if (filters?.searchTerm) {
        const term = filters?.searchTerm;
        query = query?.or(
          `company_name.ilike.%${term}%,contact_person.ilike.%${term}%,email.ilike.%${term}%,phone.ilike.%${term}%,address.ilike.%${term}%`
        );
      }

      // Filter by minimum active projects
      if (filters?.minActiveProjects !== undefined && filters?.minActiveProjects !== null) {
        query = query?.gte('active_projects', filters?.minActiveProjects);
      }

      // Filter by maximum active projects
      if (filters?.maxActiveProjects !== undefined && filters?.maxActiveProjects !== null) {
        query = query?.lte('active_projects', filters?.maxActiveProjects);
      }

      // Filter by minimum total value
      if (filters?.minTotalValue !== undefined && filters?.minTotalValue !== null) {
        query = query?.gte('total_value', filters?.minTotalValue);
      }

      // Filter by maximum total value
      if (filters?.maxTotalValue !== undefined && filters?.maxTotalValue !== null) {
        query = query?.lte('total_value', filters?.maxTotalValue);
      }

      // Filter by minimum bid conversion rate
      if (filters?.minConversionRate !== undefined && filters?.minConversionRate !== null) {
        query = query?.gte('bid_conversion_rate', filters?.minConversionRate);
      }

      // Filter by date range (created_at)
      if (filters?.startDate) {
        query = query?.gte('created_at', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('created_at', filters?.endDate);
      }

      // Sorting
      const sortBy = filters?.sortBy || 'created_at';
      const sortOrder = filters?.sortOrder === 'asc' ? true : false;
      query = query?.order(sortBy, { ascending: sortOrder });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error in advanced search');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error in advanced search:', error?.message);
      throw normalizeError(error, 'Error in advanced search');
    }
  },

  // Filter bids with advanced options
  async filterBids(filters = {}) {
    try {
      let query = supabase?.from('bids')?.select('*');

      // Search term
      if (filters?.searchTerm) {
        const term = filters?.searchTerm;
        query = query?.or(
          `customer_name.ilike.%${term}%,project_name.ilike.%${term}%,bid_number.ilike.%${term}%,project_location.ilike.%${term}%`
        );
      }

      // Filter by customer name
      if (filters?.customerName) {
        query = query?.ilike('customer_name', `%${filters?.customerName}%`);
      }

      // Filter by status
      if (filters?.status && filters?.status?.length > 0) {
        if (Array.isArray(filters?.status)) {
          query = query?.in('status', filters?.status);
        } else {
          query = query?.eq('status', filters?.status);
        }
      }

      // Filter by amount range
      if (filters?.minAmount !== undefined && filters?.minAmount !== null) {
        query = query?.gte('total_amount', filters?.minAmount);
      }

      if (filters?.maxAmount !== undefined && filters?.maxAmount !== null) {
        query = query?.lte('total_amount', filters?.maxAmount);
      }

      // Filter by date range
      if (filters?.startDate) {
        query = query?.gte('created_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('created_date', filters?.endDate);
      }

      // Sorting
      const sortBy = filters?.sortBy || 'created_at';
      const sortOrder = filters?.sortOrder === 'asc' ? true : false;
      query = query?.order(sortBy, { ascending: sortOrder });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error filtering bids');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error filtering bids:', error?.message);
      throw normalizeError(error, 'Error filtering bids');
    }
  },

  // Filter projects with advanced options
  async filterProjects(filters = {}) {
    try {
      let query = supabase?.from('projects')?.select('*');

      // Search term
      if (filters?.searchTerm) {
        const term = filters?.searchTerm;
        query = query?.or(
          `customer_name.ilike.%${term}%,project_name.ilike.%${term}%,project_number.ilike.%${term}%,location.ilike.%${term}%`
        );
      }

      // Filter by customer name
      if (filters?.customerName) {
        query = query?.ilike('customer_name', `%${filters?.customerName}%`);
      }

      // Filter by status
      if (filters?.status && filters?.status?.length > 0) {
        if (Array.isArray(filters?.status)) {
          query = query?.in('status', filters?.status);
        } else {
          query = query?.eq('status', filters?.status);
        }
      }

      // Filter by budget range
      if (filters?.minBudget !== undefined && filters?.minBudget !== null) {
        query = query?.gte('total_budget', filters?.minBudget);
      }

      if (filters?.maxBudget !== undefined && filters?.maxBudget !== null) {
        query = query?.lte('total_budget', filters?.maxBudget);
      }

      // Filter by completion percentage range
      if (filters?.minCompletion !== undefined && filters?.minCompletion !== null) {
        query = query?.gte('completion_percentage', filters?.minCompletion);
      }

      if (filters?.maxCompletion !== undefined && filters?.maxCompletion !== null) {
        query = query?.lte('completion_percentage', filters?.maxCompletion);
      }

      // Filter by date range (start_date)
      if (filters?.startDate) {
        query = query?.gte('start_date', filters?.startDate);
      }

      if (filters?.endDate) {
        query = query?.lte('end_date', filters?.endDate);
      }

      // Filter by converted from bid
      if (filters?.convertedFromBid !== undefined && filters?.convertedFromBid !== null) {
        query = query?.eq('converted_from_bid', filters?.convertedFromBid);
      }

      // Sorting
      const sortBy = filters?.sortBy || 'created_at';
      const sortOrder = filters?.sortOrder === 'asc' ? true : false;
      query = query?.order(sortBy, { ascending: sortOrder });

      const { data, error } = await query;

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw normalizeError(error, 'Schema error filtering projects');
        } else if (error?.code === 'PGRST116') {
          return { data: [], error: null };
        } else {
          console.log('Data error:', error?.message);
          return { data: null, error };
        }
      }
      return { data: toCamelCase(data), error: null };
    } catch (error) {
      console.error('Error filtering projects:', error?.message);
      throw normalizeError(error, 'Error filtering projects');
    }
  },

  // Get customer statistics and summary
  async getCustomerStats(customerId) {
    try {
      // Get customer details
      const { data: customer, error: customerError } = await supabase
        ?.from('customers')
        ?.select('*')
        ?.eq('id', customerId)
        ?.maybeSingle();

      if (customerError) {
        if (isSchemaError(customerError)) {
          throw normalizeError(customerError, 'Schema error fetching customer stats');
        }
        return { data: null, error: customerError };
      }

      if (!customer) {
        return { data: null, error: { message: 'Customer not found' } };
      }

      // Get bids for this customer
      const { data: bids } = await this.getBidsByCustomer(customer?.company_name);
      
      // Get projects for this customer
      const { data: projects } = await this.getProjectsByCustomer(customer?.company_name);

      // Calculate statistics
      const stats = {
        customer: toCamelCase(customer),
        totalBids: bids?.length || 0,
        totalProjects: projects?.length || 0,
        activeProjects: projects?.filter(p => 
          p?.status === 'In Progress' || p?.status === 'Planning'
        )?.length || 0,
        completedProjects: projects?.filter(p => p?.status === 'Completed')?.length || 0,
        totalBidValue: bids?.reduce((sum, bid) => sum + (parseFloat(bid?.totalAmount) || 0), 0),
        totalProjectValue: projects?.reduce((sum, project) => 
          sum + (parseFloat(project?.totalBudget) || 0), 0
        ),
        acceptedBids: bids?.filter(b => 
          b?.status === 'Accepted' || b?.status === 'Converted to Project' || b?.status === 'Converted to a Project'
        )?.length || 0,
        conversionRate: bids?.length > 0 
          ? Math.round((projects?.length / bids?.length) * 100) 
          : 0,
        recentBids: bids?.slice(0, 5) || [],
        recentProjects: projects?.slice(0, 5) || []
      };

      return { data: stats, error: null };
    } catch (error) {
      console.error('Error fetching customer stats:', error?.message);
      throw normalizeError(error, 'Error fetching customer stats');
    }
  }
};